import { getCompressedAssetUrl } from 'lib/config/assetUrls';

/** Bucket folder every MAX image lives in: `public-web-assets.uh-static.com/web_v2/uh-max/`. */
export const MAX_ASSET_BASE = 'web_v2/uh-max';

/** For `CustomImage`, whose `src` must NOT start with `/` — a leading slash means `/public`. */
export const maxAsset = (file: string): string => `${MAX_ASSET_BASE}/${file}`;

/** For plain `<img>` and `background-image`, where the helper needs the leading slash. */
export const maxAssetUrl = (file: string): string =>
  getCompressedAssetUrl(`/${MAX_ASSET_BASE}/${file}`);

/**
 * Explainer-modal art under `web_v2/uh-max/`: carousel slides in
 * `slides-4/{desktop,mobile}/`, bento art in `slides-4/bento/`. Staged under
 * `cdn-upload/` with the same layout until the bucket sync runs — see
 * "Uploading" in `docs/feature-ultrahuman-max.md`.
 */
export type MaxSlideVariant = 'desktop' | 'mobile';

/**
 * Full CDN URL: the modal renders plain `<img>`, not `CustomImage`.
 *
 * `slides-4` is the 2026-10-07 re-export after design re-laid the slide copy.
 * Like `slides-2` the 4x exports come straight from the component sets and are
 * **square** — `slides-1` baked a 24px corner in, which would show as a grey
 * notch once the CSS radius drops to 20px on mobile. New folder per export,
 * because SpeedSize caches by URL.
 */
export const maxSlide = (variant: MaxSlideVariant, file: string): string =>
  maxAssetUrl(`slides-4/${variant}/${file}`);

/**
 * `slides-4/bento` (a straight copy of `slides-2/bento`, kept beside the slides so
 * one folder is the whole modal), not `slides-1/bento-1`: the filenames are unchanged between
 * exports, and a new folder is the cheapest way past the SpeedSize cache, which
 * keys on URL.
 */
export const maxBentoArt = (file: string): string =>
  maxAssetUrl(`slides-4/bento/${file}`);

/**
 * SpeedSize compresses the bytes of a MAX image but does NOT resize it unless
 * the URL carries a `/w_<n>` segment — without one it returns the 2816×1680
 * master, which the browser decodes to ~19 MB of bitmap each. The explainer
 * modal renders 27 of them, so omitting this froze the renderer outright.
 * Every `<img>` for MAX art goes through `maxSrcSet`.
 */
export const maxWidth = (url: string, width: number): string =>
  `${url}/w_${width}`;

/** `cssWidth` is the CSS px the image occupies; the 2x entry covers retina. */
export const maxSrcSet = (url: string, cssWidth: number): string =>
  `${maxWidth(url, cssWidth)} 1x, ${maxWidth(url, cssWidth * 2)} 2x`;

/**
 * `w` descriptors, to be paired with a `sizes` attribute. `maxSrcSet` bakes one
 * CSS width into `x` descriptors, so the candidate tracks DPR and never the real
 * box — in the explainer modal that upscaled the bento cards 2.3x between 768px
 * and 1024px, where they go full-width but the hard-coded width is still 346.
 */
export const maxSrcSetW = (url: string, widths: number[]): string =>
  widths.map((w) => `${maxWidth(url, w)} ${w}w`).join(', ');
